/**
 * Object templates for the editor's "Add" panel.
 *
 * Templates describe a minimal recipe for creating a new SceneObject with
 * sensible defaults (mesh, default behaviors). They are deliberately dumb —
 * no parenting logic, no selection awareness, no persistence concerns. The
 * caller (sandbox, TraceFrontend, etc.) is responsible for resolving the
 * target parent and recording an undo entry.
 *
 * Sandbox-specific templates (Pages containers, particle emitter presets,
 * astronaut demo asset) stay in sandbox as thin wrappers — they don't belong
 * in the shared core.
 */

import type { PrimitiveType } from '../../export/BuiltInPrimitives';

/** Template identifier. Extend as new sharable templates are added. */
export type ObjectTemplate =
    | 'empty'
    | 'cube'
    | 'sphere'
    | 'plane'
    | 'cylinder'
    | 'text';

export interface TemplateDescriptor {
    /** Machine name used by createFromTemplate and the add-panel UI. */
    readonly template: ObjectTemplate;
    /** Human-readable label for the add panel. */
    readonly label: string;
    /** Default name for newly-created objects of this template. */
    readonly defaultName: string;
    /** If this is a built-in primitive, the underlying primitive type. */
    readonly primitive?: PrimitiveType;
    /** Behavior types to add after creation (in order). */
    readonly behaviors: readonly string[];
}

export const OBJECT_TEMPLATES: Record<ObjectTemplate, TemplateDescriptor> = {
    empty: {
        template: 'empty',
        label: 'Empty',
        defaultName: 'NewObject',
        behaviors: [],
    },
    cube: {
        template: 'cube',
        label: 'Cube',
        defaultName: 'Cube',
        primitive: 'cube',
        behaviors: ['Renderer'],
    },
    sphere: {
        template: 'sphere',
        label: 'Sphere',
        defaultName: 'Sphere',
        primitive: 'sphere',
        behaviors: ['Renderer'],
    },
    plane: {
        template: 'plane',
        label: 'Plane',
        defaultName: 'Plane',
        primitive: 'plane',
        behaviors: ['Renderer'],
    },
    cylinder: {
        template: 'cylinder',
        label: 'Cylinder',
        defaultName: 'Cylinder',
        primitive: 'cylinder',
        behaviors: ['Renderer'],
    },
    text: {
        template: 'text',
        label: 'Text',
        defaultName: 'Text',
        behaviors: ['Text'],
    },
};

/** List of templates in canonical presentation order. */
export const OBJECT_TEMPLATE_LIST: readonly TemplateDescriptor[] = [
    OBJECT_TEMPLATES.empty,
    OBJECT_TEMPLATES.cube,
    OBJECT_TEMPLATES.sphere,
    OBJECT_TEMPLATES.plane,
    OBJECT_TEMPLATES.cylinder,
    OBJECT_TEMPLATES.text,
];
